/**
 * WebSocket namespace strings for backend gateways and frontend hooks.
 *
 * Each gateway declares the namespace via `@WebSocketGateway({ namespace })`
 * (or `createWsGatewayOptions(namespace)`) and clients connect via
 * `useWebSocket({ namespace })` — both sides must agree, so the literal
 * lives here.
 *
 * Tracking-related namespaces (`dispatcher-tracking`, `portal-tracking`)
 * live in `TRACKING_WS_NAMESPACES` (`tracking-ws.types.ts`) because the
 * dispatcher and portal sides share the same event vocabulary
 * (location/status/geofence-violation).
 *
 * Slash-prefix asymmetry: `AGENT_WS_NAMESPACE` and `CHATBOT_WS_NAMESPACE`
 * historically use leading slashes (`'/agents'`, `'/ai-chatbot'`) while
 * the other namespaces don't. socket.io normalises both to the same
 * internal namespace, so the asymmetry is purely cosmetic and changing
 * the values is wire-safe — but doing so risks subtle URL-construction
 * bugs in any client that does `${wsUrl}${NAMESPACE}` instead of
 * `${wsUrl}/${NAMESPACE}`. Unification is deliberately deferred to its
 * own follow-up after a sweep of every consumer.
 *
 * Adding a new namespace: define an `as const` literal here, then add a
 * matching `typeof X_WS_NAMESPACE` arm to `WsNamespace` below. The
 * `createWsGatewayOptions` and `useWebSocket` signatures narrow on
 * `WsNamespace`, so call sites passing a typo or an un-hoisted literal
 * become a TypeScript error (#383).
 */

import type { TrackingWsNamespace } from './tms/tracking-ws.types';

export const AI_ROUTING_WS_NAMESPACE = 'ai-routing' as const;
export const DASHBOARD_WS_NAMESPACE = 'dashboard' as const;
export const NOTIFICATIONS_WS_NAMESPACE = 'notifications' as const;
export const CROSS_DOCKING_WS_NAMESPACE = 'cross-docking' as const;
export const DOCK_SCHEDULING_WS_NAMESPACE = 'dock-scheduling' as const;
export const DEVICE_MONITORING_WS_NAMESPACE = 'device-monitoring' as const;
export const LAYOUT_DESIGNER_WS_NAMESPACE = 'layout-designer' as const;
// The data-import gateway lives in `@axon/data-import` but its socket.io
// namespace is the shorter `'import'` (not `'data-import'`) — kept as the
// consumed wire identifier rather than the package directory name.
export const DATA_IMPORT_WS_NAMESPACE = 'import' as const;

// Agent runtime namespace (shared between backend-tms + backend-wms agent
// gateways). The room-scope tag `'agents'` is what `TenantRoomManager`
// stamps into the room key; the namespace `/agents` is what socket.io
// uses to route the connection. They differ by a slash because the
// historical convention split the two roles.
export const AGENT_WS_NAMESPACE = '/agents' as const;
export const AGENT_WS_ROOM_SCOPE = 'agents' as const;

export const CHATBOT_WS_NAMESPACE = '/ai-chatbot' as const;

// Backend gateway shipped — `FinanceGateway` (closes #421).
export const FINANCE_WS_NAMESPACE = 'finance' as const;

// Backend gateway shipped — `EquipmentGateway` (closes #422).
export const EQUIPMENT_WS_NAMESPACE = 'equipment' as const;

// Backend gateway shipped — `DocumentsGateway` (closes #420).
export const DOCUMENTS_WS_NAMESPACE = 'documents' as const;

/**
 * Sub-union of namespaces consumed by frontend hooks but not yet served
 * by a backend `@WebSocketGateway`. Kept as a distinct type so a `git
 * grep PendingWsNamespace` enumerates every pending gateway without
 * reading per-constant comments.
 *
 * `never` while no namespace is in this state — kept as the union slot
 * so a future un-shipped namespace can be added here without rewriting
 * the `WsNamespace` union below.
 */
export type PendingWsNamespace = never;

/**
 * Branded union of every hoisted WebSocket namespace. Consumed by
 * `createWsGatewayOptions(namespace: WsNamespace)` and
 * `useWebSocket({ namespace: WsNamespace })` so a typo or an
 * un-hoisted literal at a call site is a compile-time error.
 */
export type WsNamespace =
  | typeof AI_ROUTING_WS_NAMESPACE
  | typeof DASHBOARD_WS_NAMESPACE
  | typeof NOTIFICATIONS_WS_NAMESPACE
  | typeof CROSS_DOCKING_WS_NAMESPACE
  | typeof DOCK_SCHEDULING_WS_NAMESPACE
  | typeof DEVICE_MONITORING_WS_NAMESPACE
  | typeof LAYOUT_DESIGNER_WS_NAMESPACE
  | typeof DATA_IMPORT_WS_NAMESPACE
  | typeof AGENT_WS_NAMESPACE
  | typeof CHATBOT_WS_NAMESPACE
  | typeof FINANCE_WS_NAMESPACE
  | typeof EQUIPMENT_WS_NAMESPACE
  | typeof DOCUMENTS_WS_NAMESPACE
  | PendingWsNamespace
  | TrackingWsNamespace;
